import React, { useState, useEffect } from "react";
import { Button, Collapse } from "react-bootstrap";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { createExamThunk, fetchExamCategoriesThunk, fetchExamsThunk } from "../redux/academicCalendarSlice";
import userStore from "../../../zustand/userStore";
import { setStepIndex, setStepValid } from "../../../redux/globalJoyrideSlice";

const ExamCategoryTabs = ({ onSubmitted }) => {
  const userData = userStore((state) => state.user);
  // const [categories, setCategories] = useState([]);
  const [openCategories, setOpenCategories] = useState([]);
  const [examsByCategory, setExamsByCategory] = useState({});
  const [selectedExams, setSelectedExams] = useState({});
  const [newExamRows, setNewExamRows] = useState({});
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.academicCalendar.examCategories);
  const status = useSelector((state) => state.academicCalendar.status);

  useEffect(() => {
    dispatch(fetchExamCategoriesThunk());
  }, [dispatch,]);
  
  // useEffect(() => {
  //   if (categories.length === 0) {
  //     dispatch(fetchExamCategoriesThunk());
  //   }
  // }, [dispatch, categories]);

  useEffect(() => {
    if (categories.length > 0) {
      const initial = {};
      categories.forEach((cat) => {
        initial[cat.id] = [];
      });
      setExamsByCategory(initial);
      setNewExamRows(initial);
      setSelectedExams(initial);
    }
  }, [categories]);

  const toggleCategory = (catId) => {
    setOpenCategories((prev) =>
      prev.includes(catId) ? prev.filter((id) => id !== catId) : [...prev, catId]
    );
    dispatch(setStepIndex(2))
  };

  const handleAddRow = (catId) => {
    const newRow = {
      id: Date.now(),
      start_date: "",
      end_date: "",
      details: "",
    };
    setExamsByCategory((prev) => ({
      ...prev,
      [catId]: [...prev[catId], newRow],
    }));
  };

  const handleChange = (catId, examId, field, value) => {
    setExamsByCategory((prev) => ({
      ...prev,
      [catId]: prev[catId].map((exam) =>
        exam.id === examId ? { ...exam, [field]: value } : exam
      ),
    }));
  };

  const handleSelect = (catId, examId) => {
    const current = selectedExams[catId] || [];
    if (current.includes(examId)) {
      setSelectedExams((prev) => ({
        ...prev,
        [catId]: current.filter((id) => id !== examId),
      }));
    } else {
      setSelectedExams((prev) => ({
        ...prev,
        [catId]: [...current, examId],
      }));
    }
  };

  const handleRemove = (catId, examId) => {
    setExamsByCategory((prev) => ({
      ...prev,
      [catId]: prev[catId].filter((exam) => exam.id !== examId),
    }));
    setSelectedExams((prev) => ({
      ...prev,
      [catId]: (prev[catId] || []).filter((id) => id !== examId),
    }));
  };

  const handleSubmit = async () => {
  try {
    // ✅ 1) VALIDATION — Start date must not be after end date
    for (const catId in selectedExams) {
      const selectedIds = selectedExams[catId];
      const selectedData = examsByCategory[catId].filter((exam) =>
        selectedIds.includes(exam.id)
      );

      for (const exam of selectedData) {
        if (exam.start_date && exam.end_date) {
          const startDate = moment(exam.start_date);
          const endDate = moment(exam.end_date);

          if (startDate.isAfter(endDate)) {
            const categoryName = categories.find(c => c.id === Number(catId))?.name;

            alert(
              `Error: In category '${categoryName}', the start date cannot be after the end date for exam: '${exam.details}'.`
            );
            return;
          }
        }
      }
    }

    // ✅ 2) SUBMISSION LOGIC — Now includes holiday confirmation logic
    for (const catId in selectedExams) {
      const selectedIds = selectedExams[catId];
      const selectedData = examsByCategory[catId].filter((exam) =>
        selectedIds.includes(exam.id)
      );

      for (const exam of selectedData) {
        const payload = {
          school_id: 1,
          user_id: userData.id,
          exam_cat_id: Number(catId),
          start_date: exam.start_date,
          end_date: exam.end_date,
          details: exam.details,
        };

        // 1️⃣ FIRST API CALL
        let response;
        try {
          response = await dispatch(createExamThunk(payload)).unwrap();
        } catch (error) {
          console.error("Error creating exam:", error);
          alert("Error submitting exam. Please try again.");
          continue; // move to next exam
        }

        // 2️⃣ HOLIDAY FOUND → ASK CONFIRMATION
        if (response?.requireConfirmation) {
          const userConfirmed = window.confirm(response.message);

          // ❌ User cancelled → skip this exam, continue with next one
          if (!userConfirmed) continue;

          // 3️⃣ SECOND API CALL WITH CONFIRM FLAG
          const confirmedPayload = { ...payload, isConfirm: true };

          try {
            await dispatch(createExamThunk(confirmedPayload)).unwrap();
          } catch (error) {
            console.error("Error submitting confirmed exam:", error);
            alert("Failed to submit exam after confirmation.");
          }
        }
      }
    }

    // SUCCESS FINAL MESSAGE
    alert("Selected exams submitted successfully!");

    await dispatch(fetchExamsThunk({ user_id: userData.id }));

    dispatch(setStepIndex(8));
    
    if (onSubmitted) onSubmitted();

    // RESET STATE
    setSelectedExams({});
    setExamsByCategory((prev) => {
      const reset = {};
      for (let key in prev) reset[key] = [];
      return reset;
    });

  } catch (error) {
    console.error("Error submitting exams", error);
    alert("Failed to submit some exams.");
  }
};


  return (
    <div className="exam-category-tabs" data-tour="exam-category-list">
      {categories.map((cat) => (
        <div key={cat.id} className="mb-3">
          <Button
            variant="outline-primary"
            className="w-100 text-start"
            onClick={() => toggleCategory(cat.id)}
          >
            {cat.name}
          </Button>

          <Collapse in={openCategories.includes(cat.id)}>
            <div className="mt-2 border rounded p-3">
              {(examsByCategory[cat.id] || []).map((exam) => (
                <div key={exam.id} className="row align-items-center mb-2 gap-2 gx-2">
                  <div className="col-auto">
                    <input
                      type="checkbox"
                      checked={(selectedExams[cat.id] || []).includes(exam.id)}
                      data-tour="exam-checkbox"
                      onChange={() =>{
                         handleSelect(cat.id, exam.id);
                         dispatch(setStepIndex(7))
                      }}
                    />
                  </div>
                  <div className="col">
                    <input
                      type="date"
                      className="form-control"
                      data-tour="start-date"
                      value={exam.start_date}
                      onChange={(e) =>
                      {
                        handleChange(cat.id, exam.id, "start_date", e.target.value);
                        dispatch(setStepValid(true))
                      }
                      }
                      required
                    />
                  </div>
                  <div className="col">
                    <input
                      type="date"
                      className="form-control"
                      data-tour="end-date"
                      value={exam.end_date}
                      onChange={(e) =>
                      {
                        handleChange(cat.id, exam.id, "end_date", e.target.value);
                        dispatch(setStepValid(true))
                      }
                      }
                      required
                    />
                  </div>
                  <div className="col">
                    <input
                      type="text"
                      className="form-control"
                      data-tour="proposed-exam-details"
                      value={exam.details}
                      onChange={(e) =>
                      {
                        handleChange(cat.id, exam.id, "details", e.target.value);
                        dispatch(setStepValid(true))
                      }
                      }
                      placeholder="Exam Details"
                      required
                    />
                  </div>
                  <div className="col-auto">
                    <button
                      className="btn m-0 btn-sm btn-danger"
                      onClick={() => handleRemove(cat.id, exam.id)}
                    >
                      X
                    </button>
                  </div>
                </div>
              ))}

              <Button
                variant="outline-success"
                data-tour="add-exam-btn"
                size="sm"
                onClick={() => {
                  handleAddRow(cat.id);
                  dispatch(setStepIndex(3))
                }}
              >
                + Add Exam
              </Button>
            </div>
          </Collapse>
        </div>
      ))}

      {Object.values(selectedExams).some((arr) => arr.length > 0) && (
        <div className="mt-4 text-end">
          <button className="btn btn-primary" onClick={handleSubmit} data-tour="exam-submit">
            Submit Selected Exams
          </button>
        </div>
      )}
    </div>
  );
};

export default ExamCategoryTabs;
